import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'
import {
  AlertCircle,
  CheckCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Info,
} from 'lucide-react'
import Sidebar from '../components/layout/Sidebar'
import StepShell from '../components/meetingBuilder/StepShell'
import { meetingsAPI } from '../services/api'
import { useToast } from '../components/common/Toast'
import { useAuth } from '../context/AuthContext'
import { isExternalJoinEnabled } from '../config/externalJoinBeta'
import { parseExternalMeetingUrl } from '../shared/externalMeetingUrlParser'
import { builderInputClassName } from '../components/meetingBuilder/builderFieldStyles'

/**
 * External-meeting setup wizard for the "Add Rithmo to a Meeting" flow.
 * Tier 1: wizard chrome + Step 1 (URL). Steps 2 (meeting type) and 3 (review)
 * land in subsequent tiers per docs/teams_join_setup_redesign.md.
 *
 * Visual chrome mirrors MeetingBuilderPage (`/agenda`) — centered glassmorphism
 * card, teal progress bar, StepShell typography — so the Teams join experience
 * reads as a sibling of the Zoom Host wizard.
 */

const EMPTY_FORM = {
  title: '',
  joinUrl: '',
  scheduledStartTime: '',
}

// Total wizard steps. Step 1 = URL paste + choose path (this page).
// Step 2 = brief chat (lives in ExternalMeetingBriefPage at
// /join-external/brief/:meetingId).
const TOTAL_STEPS = 2

export default function ExternalMeetingSetupPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const editMeetingId = searchParams.get('edit')
  const toast = useToast()
  const { user } = useAuth()

  const [form, setForm] = useState(EMPTY_FORM)
  const [urlValidation, setUrlValidation] = useState(null) // null | { valid, reason }
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [saving, setSaving] = useState(false)
  const [publishing, setPublishing] = useState(false)
  const [savedMeetingId, setSavedMeetingId] = useState(editMeetingId || null)
  const [loadingEdit, setLoadingEdit] = useState(!!editMeetingId)

  // Beta gate: redirect non-beta users immediately
  useEffect(() => {
    if (user && !isExternalJoinEnabled(user.email)) {
      navigate('/dashboard', { replace: true })
    }
  }, [user, navigate])

  // Load existing meeting when editing
  useEffect(() => {
    if (!editMeetingId) return
    setLoadingEdit(true)
    meetingsAPI
      .get(editMeetingId)
      .then((res) => {
        const m = res?.data ?? res
        if (!m) return
        setForm({
          title: m.title ?? '',
          joinUrl: m.joinUrl ?? m.join_url ?? '',
          scheduledStartTime: m.scheduledStartTime
            ? m.scheduledStartTime.slice(0, 16)
            : '',
        })
        if (m.joinUrl || m.join_url) {
          const parsed = parseExternalMeetingUrl(m.joinUrl ?? m.join_url)
          setUrlValidation(parsed)
        }
      })
      .catch(() => {
        toast.error('Could not load meeting', 'Please check the link and try again.')
      })
      .finally(() => setLoadingEdit(false))
  }, [editMeetingId]) // eslint-disable-line react-hooks/exhaustive-deps

  function handleUrlChange(e) {
    const val = e.target.value
    setForm((f) => ({ ...f, joinUrl: val }))
    if (!val.trim()) {
      setUrlValidation(null)
      return
    }
    const parsed = parseExternalMeetingUrl(val.trim())
    // This page accepts Microsoft Teams AND Google Meet URLs — both external
    // Recall-driven providers. Zoom users go through "Host a Meeting" at
    // /agenda instead. If they happen to paste a valid Zoom URL here, reject
    // it with a helpful redirect.
    if (parsed.valid && parsed.provider !== 'teams' && parsed.provider !== 'meet') {
      setUrlValidation({
        valid: false,
        reason: 'This page accepts Microsoft Teams and Google Meet links. For Zoom meetings, use "Host a Meeting" on the dashboard.',
      })
      return
    }
    setUrlValidation(parsed)
  }

  function buildPayload(isDraft) {
    const payload = {
      title: form.title.trim() || 'External Meeting',
      meetingJoinKind: 'assistant_external',
      joinUrl: form.joinUrl.trim(),
      isDraft,
      isRecurring: false,
    }
    if (form.scheduledStartTime) {
      payload.scheduledStartTime = new Date(form.scheduledStartTime).toISOString()
    }
    return payload
  }

  async function saveDraft() {
    if (!form.joinUrl.trim()) {
      toast.error('Meeting link required', 'Please paste a Teams or Google Meet meeting URL.')
      return null
    }
    if (urlValidation && !urlValidation.valid) {
      toast.error('Invalid meeting link', urlValidation.reason ?? 'Please paste a Teams or Google Meet meeting URL.')
      return null
    }
    setSaving(true)
    try {
      let id = savedMeetingId
      if (id) {
        await meetingsAPI.update(id, buildPayload(true))
      } else {
        const res = await meetingsAPI.create(buildPayload(true))
        id = res?.data?.meetingId ?? res?.data?.id ?? res?.id
        setSavedMeetingId(id)
      }
      toast.success('Draft saved', 'Your meeting setup has been saved.')
      return id
    } catch (err) {
      toast.error('Save failed', err?.message ?? 'Could not save. Please try again.')
      return null
    } finally {
      setSaving(false)
    }
  }

  /**
   * Validate the URL before any submit branch. Returns true if good.
   */
  function urlReadyOrToast() {
    if (!form.joinUrl.trim()) {
      toast.error('Meeting link required', 'Please paste your Teams or Google Meet meeting URL.')
      return false
    }
    if (urlValidation && !urlValidation.valid) {
      toast.error('Invalid meeting link', urlValidation.reason ?? 'Please paste a valid Teams or Google Meet URL.')
      return false
    }
    return true
  }

  /**
   * Primary path: persist a draft and hand off to the brief chat page. The
   * brief page publishes the meeting once the user finishes (or skips) the
   * chat. Keeps the meeting as draft until then so a half-done brief doesn't
   * dispatch a bot.
   */
  async function handleBriefFirst() {
    if (!urlReadyOrToast()) return
    setSaving(true)
    try {
      let id = savedMeetingId
      if (id) {
        await meetingsAPI.update(id, buildPayload(true))
      } else {
        const res = await meetingsAPI.create(buildPayload(true))
        id = res?.data?.meetingId ?? res?.data?.id ?? res?.id
        if (!id) throw new Error('No meeting ID returned')
        setSavedMeetingId(id)
      }
      navigate(`/join-external/brief/${id}`)
    } catch (err) {
      toast.error('Could not continue', err?.message ?? 'Please try again.')
    } finally {
      setSaving(false)
    }
  }

  /**
   * Secondary path (text link): publish immediately with defaults. Bot
   * dispatched right away; user lands on MeetingDetailPage. No brief captured.
   */
  async function handleAddWithoutBriefing() {
    if (!urlReadyOrToast()) return
    setPublishing(true)
    try {
      let id = savedMeetingId
      if (id) {
        await meetingsAPI.update(id, buildPayload(false))
      } else {
        const res = await meetingsAPI.create(buildPayload(false))
        id = res?.data?.meetingId ?? res?.data?.id ?? res?.id
        if (!id) throw new Error('No meeting ID returned')
        setSavedMeetingId(id)
      }
      navigate(`/meeting/${id}`)
    } catch (err) {
      toast.error('Setup failed', err?.message ?? 'Could not set up meeting.')
    } finally {
      setPublishing(false)
    }
  }

  const urlIsInvalid = urlValidation !== null && !urlValidation.valid
  const urlIsValid = urlValidation !== null && urlValidation.valid
  const canPublish = !!form.joinUrl.trim() && !urlIsInvalid && !saving && !publishing

  if (loadingEdit) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#16c1b2]" />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Sidebar />

      <main className="lg:ml-[280px] relative overflow-hidden p-4 pt-20 lg:p-8 lg:pt-8">
        {/* Background ambient gradient — same as MeetingBuilderPage */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(circle_at_top,rgba(22,193,178,0.12),transparent_62%)]" />
        <div className="pointer-events-none absolute right-0 top-20 h-64 w-64 rounded-full bg-[#e4faf8] blur-3xl opacity-60" />

        <div className="relative mx-auto max-w-3xl">
          <div className="mb-6">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
            >
              <ArrowLeft className="w-4 h-4" />
              Dashboard
            </Link>
          </div>

          <div className="glass rounded-[32px] p-6 sm:p-8 lg:p-10">
            {/* Top action bar — Back + Save Draft, mirrors MeetingBuilderPage */}
            <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 pb-4 sm:gap-3">
              <button
                type="button"
                disabled
                className="inline-flex shrink-0 items-center gap-2 rounded-full border border-gray-100 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-300 cursor-not-allowed"
                title="You're at the start"
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>

              <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={saveDraft}
                  disabled={saving || publishing}
                  className="rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60 inline-flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  Save Draft
                </button>
              </div>
            </div>

            {/* Progress bar — teal gradient, matches MeetingBuilderPage */}
            <div className="mt-4">
              <div
                className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200/90"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={Math.round((1 / TOTAL_STEPS) * 100)}
                aria-label="Progress in setup"
              >
                <div
                  className="h-full rounded-full transition-[width] duration-300 ease-out"
                  style={{
                    width: `${(1 / TOTAL_STEPS) * 100}%`,
                    background: 'linear-gradient(135deg, #16c1b2 0%, #0b6fa4 100%)',
                  }}
                />
              </div>
            </div>

            {/* Step body */}
            <div className="min-h-0 mt-6 sm:mt-8">
              <AnimatePresence mode="wait">
                <StepShell
                  key="step-1-url"
                  eyebrow="Step 1"
                  title="Add your meeting"
                  description="Rithmo joins silently — the host admits it from the lobby. Works with Microsoft Teams and Google Meet."
                >
                  <div className="mx-auto w-full max-w-xl space-y-5 text-left">
                    {/* URL field — primary. No label; placeholder + title carry the intent. */}
                    <div>
                      <div className="relative">
                        <input
                          type="url"
                          placeholder="https://teams.microsoft.com/l/meetup-join/… or https://meet.google.com/…"
                          value={form.joinUrl}
                          onChange={handleUrlChange}
                          className={`${builderInputClassName(urlIsInvalid, 'compact')} pr-10`}
                          aria-label="Microsoft Teams or Google Meet link"
                          required
                        />
                        {urlIsValid && (
                          <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-green-500" />
                        )}
                        {urlIsInvalid && (
                          <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-400" />
                        )}
                      </div>
                      {urlIsInvalid && (
                        <p className="mt-1.5 text-xs text-red-600">{urlValidation.reason}</p>
                      )}
                    </div>

                    {/* Lobby notice — always shown for valid Teams/Meet URLs */}
                    {urlIsValid && (
                      <div className="flex items-start gap-3 px-4 py-3 rounded-2xl border border-[#16c1b2]/20 bg-[#f0fdfc]">
                        <Info className="w-4 h-4 text-[#0d8f84] shrink-0 mt-0.5" />
                        <p className="text-sm text-[#0d8f84] leading-relaxed">
                          <strong>Lobby admission required.</strong> &ldquo;Rithmo
                          Transcription&rdquo; joins the {urlValidation?.provider === 'meet' ? 'Meet' : 'Teams'} lobby.
                          The host needs to {urlValidation?.provider === 'meet' ? 'approve' : 'admit'} it
                          before transcription begins.
                        </p>
                      </div>
                    )}

                    {/* Advanced options — collapses everything that isn't the URL.
                        Title and scheduled start are both optional; defaults
                        kick in when blank. */}
                    <div className="border-t border-gray-100 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowAdvanced((v) => !v)}
                        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition"
                      >
                        {showAdvanced ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                        Advanced options
                      </button>
                      {showAdvanced && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          className="mt-4 space-y-5"
                        >
                          {/* Title */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Meeting title <span className="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <input
                              type="text"
                              placeholder="e.g. Quarterly sync with Acme"
                              value={form.title}
                              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                              className={builderInputClassName(false, 'compact')}
                            />
                          </div>

                          {/* Scheduled time */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1.5">
                              Scheduled start time <span className="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <input
                              type="datetime-local"
                              value={form.scheduledStartTime}
                              onChange={(e) => setForm((f) => ({ ...f, scheduledStartTime: e.target.value }))}
                              className={builderInputClassName(false, 'compact')}
                            />
                            <p className="mt-1 text-xs text-gray-400">
                              Used to score time-discipline after the meeting.
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </div>
                  </div>
                </StepShell>
              </AnimatePresence>
            </div>

            {/* Footer — Cancel on the left, primary "Brief Rithmo first →" on
                the right, with a subtle text-link escape hatch beside it for
                the user who doesn't want to chat. */}
            <div className="mt-8 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-5">
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                className="text-sm text-gray-500 hover:text-gray-700 transition px-3 py-2"
              >
                Cancel
              </button>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleAddWithoutBriefing}
                  disabled={!canPublish}
                  className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {publishing && <Loader2 className="w-4 h-4 animate-spin" />}
                  Add Rithmo without briefing
                </button>
                <button
                  type="button"
                  onClick={handleBriefFirst}
                  disabled={!canPublish}
                  className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white transition disabled:cursor-not-allowed disabled:opacity-50"
                  style={{
                    background: canPublish
                      ? 'linear-gradient(135deg, #16c1b2 0%, #0b6fa4 100%)'
                      : '#9ca3af',
                  }}
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {saving ? 'Saving…' : 'Brief Rithmo first'}
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
